import { useQuery } from "@tanstack/react-query"
import { Copy, ExternalLink, Link2 } from "lucide-react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { getEntryLinkStats, getFlowStats, statsKeys } from "@/entities/bot-stats"
import { StatusBadge } from "@/shared/ui/status-badge"
import { Button } from "@/shared/ui/button"
import { EmptyState } from "@/shared/ui/empty-state"
import { botPath, type LoadedDraft, useBotWorkspace, WithDraft, WorkspaceSection } from "@/widgets/bot-workspace"
import { isQuietLink, QUIET_DAYS } from "@/features/manage-utm-links"
import { FilteredPeople } from "@/widgets/audience-list"
import { stepTitle, type StepSelection, UtmFunnelSteps } from "./utm-funnel-steps"
import { UtmLinkSummary } from "./utm-link-summary"

/** «Аналитика → UTM-метки → метка»: откуда люди, докуда дошли по воронке и кто они. */
export function UtmLinkPage() {
	const { code = "" } = useParams()
	return (
		<WithDraft title="UTM-метка">{(state) => <UtmLinkView state={state} code={code} />}</WithDraft>
	)
}

function UtmLinkView({ state, code }: { state: LoadedDraft; code: string }) {
	const { bot } = useBotWorkspace()
	const { draft } = state
	const [params, setParams] = useSearchParams()
	const link = (draft.entryLinks ?? []).find((item) => item.code === code)

	const flowStats = useQuery({
		queryKey: statsKeys.flows(bot.id, code),
		queryFn: () => getFlowStats(bot.id, code),
		refetchInterval: 60_000,
	})
	const linkStats = useQuery({
		queryKey: statsKeys.entryLinks(bot.id),
		queryFn: () => getEntryLinkStats(bot.id),
		select: (data) => data.links.find((item) => item.code === code),
	})
	const visits = { data: linkStats.data?.count ?? 0 }

	if (!link) {
		return (
			<WorkspaceSection title="Метка не найдена">
				<EmptyState icon={Link2} title={`Метки «${code}» нет`}>
					Возможно, её удалили или поменяли код.
				</EmptyState>
			</WorkspaceSection>
		)
	}

	const flow = link.flowId
		? draft.flows.find((item) => item.id === link.flowId)
		: draft.flows.find((item) => item.kind === "MAIN")
	const url = `https://t.me/${bot.username}?start=${link.code}`

	const stepId = params.get("step")
	const stepMode = params.get("mode") === "stuck" ? "stuck" : "passed"
	const selected: StepSelection | null = stepId ? { nodeId: stepId, mode: stepMode } : null
	const select = (next: StepSelection | null) =>
		setParams(next ? { step: next.nodeId, mode: next.mode } : {}, { replace: true })

	const node = selected ? draft.nodes.find((item) => item.id === selected.nodeId) : undefined
	const peopleTitle = node
		? `${selected?.mode === "stuck" ? "Стоят на шаге" : "Прошли шаг"} «${stepTitle(node, draft)}»`
		: "Все, кто перешёл по метке"

	return (
		<WorkspaceSection
			title={link.name}
			subtitle={
				<span className="block space-y-1">
					<span className="block font-mono break-all text-foreground">
						t.me/{bot.username}?start={link.code}
					</span>
					<span className="flex flex-wrap items-center gap-2">
						<span>Воронка: {flow?.name ?? "удалённая воронка"}</span>
						{!link.active ? (
							<StatusBadge tone="warning" size="tag">
								выключена
							</StatusBadge>
						) : null}
					</span>
					{link.comment ? <span className="block text-xs">{link.comment}</span> : null}
				</span>
			}
			actions={
				<>
					<Button
						variant="outline"
						className="rounded-xl"
						onClick={() =>
							void navigator.clipboard.writeText(url).then(() => toast.success("Ссылка скопирована"))
						}
					>
						<Copy /> Скопировать ссылку
					</Button>
					{flow ? (
						<Button asChild variant="outline" className="rounded-xl">
							<Link to={`${botPath(bot.id, `constructor/funnels/${flow.id}`)}?utm=${encodeURIComponent(code)}`}>
								<ExternalLink /> Открыть воронку
							</Link>
						</Button>
					) : null}
				</>
			}
		>
			{isQuietLink(link.active, linkStats.data?.lastAt) ? (
				<div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm">
					По метке больше {QUIET_DAYS} дней никто не переходил. Проверьте, стоит ли ещё ссылка там,
					куда её поставили, и не изменилась ли она.
				</div>
			) : null}
			<UtmLinkSummary botId={bot.id} code={code} lastAt={linkStats.data?.lastAt} />

			{flow ? (
				<section className="space-y-3">
					<h3 className="text-sm font-semibold">
						Путь по воронке «{flow.name}»
						<span className="ml-2 font-normal text-muted-foreground">
							Нажмите на число, чтобы увидеть этих людей
						</span>
					</h3>
					<UtmFunnelSteps
						draft={draft}
						flowId={flow.id}
						stats={flowStats.data}
						total={visits.data ?? 0}
						selected={selected}
						onSelect={select}
					/>
				</section>
			) : null}

			<FilteredPeople
				botId={bot.id}
				title={peopleTitle}
				emptyText="По этой метке ещё никто не заходил."
				broadcastName={`${link.name}: ${peopleTitle.charAt(0).toLowerCase()}${peopleTitle.slice(1)}`}
				filter={{
					entryLink: code,
					...(selected
						? selected.mode === "stuck"
							? { stuckNodeId: selected.nodeId }
							: { passedNodeId: selected.nodeId }
						: {}),
				}}
				onReset={selected ? () => select(null) : undefined}
			/>
		</WorkspaceSection>
	)
}
